import React, { useMemo } from 'react';
import { Box, Flex, Skeleton } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import StatusBadge from '../../../../../components/StatusBadge';
import { CypressIds } from '../../../../../constants/cypressIds';
import { InsuredProfilePageDuskIds } from '../../../../../constants/duskIds';
import { Statuses } from '../../../../../constants/statuses';
import { useInsuredActiveRecordsData, useInsuredData, useInsuredOverallStatusData } from '../../../../../services/insured-service';
import { getInfiniteListData } from '../../../../../services/util';

const OverallStatusBar = ({ insuredId }) => {
  const { data: insuredOverallStatus, isLoading: statusIsLoading, isFetching: overallStatusIsFetching } = useInsuredOverallStatusData(insuredId);
  const { data: activeRecords, isLoading: activeRecordsLoading } = useInsuredActiveRecordsData({ insuredId });
  const { data: insuredData } = useInsuredData(insuredId);

  const overallStatus = useMemo(() => {
    if (insuredData?.deletedAt) {
      return Statuses.Deleted;
    }

    if (!activeRecordsLoading) {
      const activeRecordsData = getInfiniteListData(activeRecords);

      if (insuredOverallStatus?.status !== Statuses.Archived && !activeRecordsData.length) {
        return Statuses.Active;
      }

      if (insuredOverallStatus?.status === Statuses.Archived) {
        return Statuses.Archived;
      }

      if (activeRecordsData.every(r => r.recordStatus === Statuses.Approved)) {
        return Statuses.Approved;
      }

      return Statuses.NotApproved;
    }

    return null;
  }, [activeRecords, insuredOverallStatus, insuredData]);

  if (statusIsLoading && !overallStatus) {
    return (
      <Flex alignItems="center">
        <Skeleton>
          <Box
            bgColor="greyScale.700"
            borderRadius={8}
            color="white"
            minWidth="135px"
            ml={4}
            px={2}
            py={1}
          >
            Loading...
          </Box>
        </Skeleton>
      </Flex>
    );
  }

  return (
    <Flex alignItems="center">
      <StatusBadge
        cypressId={CypressIds.insuredOverallStatus}
        duskSelector={InsuredProfilePageDuskIds.insuredOverallStatusBadge}
        isFetching={(insuredOverallStatus?.isLoading) && !insuredData?.deletedAt}
        size="md"
        status={overallStatus}
      />
    </Flex>
  );
};

OverallStatusBar.propTypes = { insuredId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

export default OverallStatusBar;
